import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

interface EvidenceFormat {
  formatId: number;
  formatName?: string;
  expectedCount: number;
  actualCount: number;
  discrepancyReason: string | null;
}

export interface EvidenceResponse {
  assignment: {
    id: number;
    campaignId: number;
    storeId: number;
    plannedQuantity: number;
    deliveredQuantity: number;
  };
  storeName: string;
  campaignName: string;
  proofImages: Array<{
    id: number;
    url: string;
    publicId: string;
    uploadedAt: Date | string;
  }>;
  formatEvidence: EvidenceFormat[];
  notes: string | null;
  signatureUrl?: string | null; // ✅ NEW: Manager signature URL
}

export interface EvidenceResult extends EvidenceResponse {
  resolvedAssignmentId: number;
  attemptedAssignmentIds: number[];
}

export function useAssignmentEvidence(assignmentCandidates: Array<number | null | undefined>) {
  const ids = useMemo(() => {
    const unique = new Set<number>();
    assignmentCandidates.forEach((id) => {
      if (typeof id === 'number' && Number.isFinite(id)) {
        unique.add(id);
      }
    });
    return Array.from(unique);
  }, [assignmentCandidates]);

  const queryResult = useQuery<EvidenceResult>({
    queryKey: ['assignment-evidence', ...ids],
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const attempted: number[] = [];
      let lastErrorText = '';

      for (const id of ids) {
        attempted.push(id);
        try {
          const response = await apiFetch(`/api/mobile/assignment/${id}/evidence`);
          if (response.ok) {
            const json = (await response.json()) as EvidenceResponse & {
              resolvedAssignmentId?: number;
              attemptedAssignmentIds?: number[];
            };

            const resolvedId = json.resolvedAssignmentId ?? id;
            const attemptedFromServer = json.attemptedAssignmentIds ?? [];
            const mergedAttempted = Array.from(new Set([...attempted, ...attemptedFromServer]));

            return {
              ...json,
              resolvedAssignmentId: resolvedId,
              attemptedAssignmentIds: mergedAttempted,
            };
          }

          const errorText = await response.text().catch(() => response.statusText || 'Unknown error');
          lastErrorText = errorText || response.statusText || `HTTP ${response.status}`;

          if (response.status === 404) {
            // Continue to next candidate
            continue;
          }

          throw new Error(lastErrorText);
        } catch (error) {
          lastErrorText = error instanceof Error ? error.message : String(error);
          // Continue to next candidate
        }
      }

      const error = new Error(lastErrorText || 'Evidence not found for provided assignment IDs');
      (error as Error & { attemptedIds?: number[] }).attemptedIds = attempted;
      throw error;
    },
  });

  return {
    ...queryResult,
    attemptedAssignmentIds: (queryResult.error as Error & { attemptedIds?: number[] })?.attemptedIds || ids,
  };
}


